import React, { forwardRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import Tooltip from '../../ui/Tooltip';
import { formatChip, isFilterEmpty } from './registry';
import { getRenderer } from './renderers';

/**
 * One expandable card in the filter panel: the field label (plus a one-line summary of the
 * value while collapsed), a per-card Clear, and the field's renderer as the body.
 */
const FilterCard = forwardRef(({ field, value, onChange, onClear, defaultExpanded }, toggleRef) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const active = !isFilterEmpty(field, value);
    const summary = active ? formatChip(field, value) : '';
    const bodyId = `ff-card-${field.key}`;
    const Renderer = getRenderer(field.type);

    return (
        <div
            className={cn(
                'rounded-lg border bg-white',
                active ? 'border-primary-200 ring-1 ring-primary-100' : 'border-slate-200'
            )}
        >
            <div className="flex items-center">
                <button
                    ref={toggleRef}
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={bodyId}
                    onClick={() => setExpanded((e) => !e)}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-300"
                >
                    <span className="shrink-0 text-sm font-medium text-slate-900">{field.label}</span>
                    {active && !expanded && (
                        <span className="min-w-0 flex-1 truncate text-right text-xs text-primary-700" title={summary}>
                            {summary}
                        </span>
                    )}
                    <ChevronDown
                        aria-hidden="true"
                        className={cn(
                            'ml-auto h-4 w-4 shrink-0 text-slate-400 motion-safe:transition-transform',
                            expanded && 'rotate-180'
                        )}
                    />
                </button>
                {active && (
                    <Tooltip label={`Clear ${field.label}`}>
                        <button
                            type="button"
                            onClick={onClear}
                            aria-label={`Clear ${field.label} filter`}
                            className="mr-1.5 grid h-7 w-7 shrink-0 place-content-center rounded-md text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-800 focus-visible:ring-2 focus-visible:ring-primary-300"
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </Tooltip>
                )}
            </div>
            {expanded && (
                <div id={bodyId} className="border-t border-slate-100 px-2 pb-2.5 pt-2">
                    <Renderer field={field} value={value} onChange={onChange} />
                </div>
            )}
        </div>
    );
});

FilterCard.displayName = 'FilterCard';

export default FilterCard;
